import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Row,
  Col,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  Label,
} from 'reactstrap';
import { useIntl } from 'react-intl';

const MB_IN_BYTES = 1024 * 1024;
const GB_IN_BYTES = 1024 * MB_IN_BYTES;
const TB_IN_BYTES = 1024 * GB_IN_BYTES;

const AdminPanelAccountEditForm = ({
  coupons,
  client,
  errors,
  handleChange,
}) => {
  const [storageLimit, setStorageLimit] = useState(null);
  const [storageUnit, setStorageUnit] = useState('gb');

  const intl = useIntl();

  const clientDiscount = (client.discounts && client.discounts.length === 1) ?
    client.discounts[0].coupon : null;
  const discountsDisabled = client.discounts && client.discounts.length > 1;

  const getStorageUnit = useCallback(() => {
    const storage = client.storage_limit;
    if (storage > TB_IN_BYTES) {
      return 'tb';
    }
    return 'gb';
  }, [client.storage_limit]);

  const updateStorageLimit = useCallback((storage) => {
    if (storageUnit === 'gb') {
      setStorageLimit(storage / GB_IN_BYTES);
    }
    if (storageUnit === 'tb') {
      setStorageLimit(storage / TB_IN_BYTES);
    }
  }, [storageUnit]);

  const handleChangeStorageUnit = (e) => {
    setStorageUnit(e.target.value);

    let newLimit = 0;
    if (e.target.value === 'gb') {
      newLimit = storageLimit * GB_IN_BYTES;
    }
    if (e.target.value === 'tb') {
      newLimit = storageLimit * TB_IN_BYTES;
    }
    handleChange({
      target: {
        name: 'storage_limit',
        value: newLimit,
      },
    });
  };

  const handleChangeStorageLimit = (e) => {
    setStorageLimit(e.target.value);

    let limit = 0;
    if (storageUnit === 'gb') {
      limit = e.target.value * GB_IN_BYTES;
    }
    if (storageUnit === 'tb') {
      limit = e.target.value * TB_IN_BYTES;
    }

    handleChange({
      target: {
        name: 'storage_limit',
        value: limit,
      },
    });
  };

  useEffect(() => {
    setStorageUnit(getStorageUnit());
  }, [getStorageUnit]);

  useEffect(() => {
    updateStorageLimit(client.storage_limit);
  }, [updateStorageLimit, client.storage_limit]);

  if (storageLimit === null) {
    return null;
  }

  const accountTypes = {
    main: 'regular',
    nonprofit: 'non-profit',
    startup: 'startup',
    education: 'education',
    student: 'student',
    agency: 'agency',
    freelancer: 'freelancer',
    realestate: 'real estate',
  };

  return (
    <>
      <Row className="mb-4">
        <Col sm={4}>
          <FormGroup>
            <Label for="inpSubdomain">
              {intl.formatMessage({ defaultMessage: 'Subdomain' })}
            </Label>
            <Input
              type="text"
              id="inpSubdomain"
              name="subdomain"
              disabled
              defaultValue={client.subdomain}
              onChange={handleChange}
              invalid={errors.includes('subdomain')}
            />
            {errors.includes('name') && (
              <FormFeedback>{intl.formatMessage({ defaultMessage: 'This field is required' })}</FormFeedback>
            )}
          </FormGroup>
        </Col>
        <Col sm={4}>
          <FormGroup>
            <Label for="inpName">
              {intl.formatMessage({ defaultMessage: 'Name' })}
            </Label>
            <Input
              type="text"
              id="inpName"
              name="name"
              defaultValue={client.name}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>
        <Col sm={4}>
          <FormGroup>
            <Label for="inpType">
              {intl.formatMessage({ defaultMessage: 'Account Type' })}
            </Label>
            <Input
              type="select"
              id="inpType"
              name="type"
              className="custom-select"
              defaultValue={client.type}
              onChange={handleChange}
            >
              {Object.keys(accountTypes).map(key => (
                <option key={key} value={key}>{accountTypes[key]}</option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col sm={6}>
          <FormGroup>
            <Label for="inpPaymentType">
              {intl.formatMessage({ defaultMessage: 'Payment Type' })}
            </Label>
            <Input
              type="select"
              id="inpPaymentType"
              name="payment_type"
              className="custom-select"
              defaultValue={client.payment_type}
              onChange={handleChange}
            >
              <option value="">regular</option>
              <option value="complimentary">complimentary</option>
              <option value="manual">manual</option>
            </Input>
          </FormGroup>
        </Col>
        <Col sm={6}>
          <FormGroup>
            <Label for="inpStorage">
              {intl.formatMessage({ defaultMessage: 'Storage Limit' })}
            </Label>
            <div className="storage-fields-wrapper">
              <Input
                type="number"
                id="inpStorage"
                name="storage_limit"
                defaultValue={storageLimit}
                onChange={handleChangeStorageLimit}
              />
              <Input
                type="select"
                className="ml-1"
                id="inpStorageUnit"
                name="storageUnit"
                defaultValue={storageUnit}
                onChange={handleChangeStorageUnit}
              >
                <option value="gb">GB</option>
                <option value="tb">TB</option>
              </Input>
            </div>
          </FormGroup>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col sm={4}>
          <FormGroup>
            <Label for="inpTrialEndsAt">
              {intl.formatMessage({ defaultMessage: 'Trial Ends' })}
            </Label>
            <Input
              type="date"
              id="inpTrialEndsAt"
              name="trial_ends_at"
              defaultValue={client.trial_ends_at ? client.trial_ends_at.substring(0, 10) : null}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>
        <Col sm={4}>
          <FormGroup>
            <Label for="inpDiscount">
              {intl.formatMessage({ defaultMessage: 'Discount' })}
              {discountsDisabled && (
                <FormText color="muted">
                  {intl.formatMessage({ defaultMessage: 'This client has multiple discounts, please contact Bart' })}
                </FormText>
              )}
            </Label>
            <Input
              type="select"
              id="inpDiscount"
              name="discount"
              defaultValue={clientDiscount}
              disabled={discountsDisabled}
              onChange={handleChange}
            >
              <option value="">-- none --</option>
              {Object.keys(coupons).map(coupon => (
                <option key={coupon} value={coupon}>{coupons[coupon]}</option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col sm={4}>
          <FormGroup>
            <Label for="inpPricingDate">
              {intl.formatMessage({ defaultMessage: 'Pricing' })}
            </Label>
            <Input
              type="select"
              id="inpPricingDate"
              name="pricing_date"
              defaultValue={client.pricing_date || ''}
              onChange={handleChange}
            >
              <option value="">current pricing</option>
              {Object.keys(client.pricing_dates || {}).map(date => (
                <option key={date} value={date}>{client.pricing_dates[date]}</option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col sm={4}>
          <FormGroup check>
            <Label for="inpAPIButton" check>
              <Input
                type="checkbox"
                id="inpAPIButton"
                name="public_api_enabled"
                value={true}
                defaultChecked={client.public_api_enabled === 1}
                onChange={handleChange}
              />
              {intl.formatMessage({ defaultMessage: 'Enable Public API' })}
            </Label>
          </FormGroup>
        </Col>
        <Col sm={4}>
          <FormGroup check>
            <Label for="inpDesktopSyncButton" check>
              <Input
                type="checkbox"
                id="inpDesktopSyncButton"
                name="desktop_sync_enabled"
                value={true}
                defaultChecked={client.desktop_sync_enabled === 1}
                onChange={handleChange}
              />
              {intl.formatMessage({ defaultMessage: 'Enable Desktop Sync' })}
            </Label>
          </FormGroup>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col sm={4}>
          <FormGroup check>
            <Label for="inpG2ReviewButton" check>
              <Input
                type="checkbox"
                id="inpG2ReviewButton"
                name="g2_review_button_hidden"
                value={true}
                defaultChecked={client.g2_review_button_hidden === 1}
                onChange={handleChange}
              />
              {intl.formatMessage({ defaultMessage: 'Hide G2 Review button' })}
            </Label>
          </FormGroup>
        </Col>
      </Row>
    </>
  );
};

const mapStateToProps = state => ({
  coupons: state.App.coupons,
});

AdminPanelAccountEditForm.propTypes = {
  coupons: PropTypes.object.isRequired,
  client: PropTypes.object.isRequired,
  errors: PropTypes.array.isRequired,
  handleChange: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(AdminPanelAccountEditForm);
